import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import NeoModal from "../../../../components/Common/NeoModal";
import NeoInput from "../../../../components/Common/NeoInput";
import NeoButton from "../../../../components/Common/NeoButton";
import NeoBadge from "../../../../components/Common/NeoBadge";
import { PRIORITY_COLORS, TICKET_CHANNELS } from "../constants";

const TicketDetailModal = ({
    isOpen,
    toggle,
    ticket,
    newComment,
    onChangeComment,
    onAddComment,
    onUpdateStatus
}) => {
    if (!ticket) return null;

    const channelLabel = TICKET_CHANNELS.find(c => c.value === ticket.channel)?.label || ticket.channel || "—";

    const detailsFooter = (
        <>
            <div className="d-flex gap-2">
                {ticket.status === "open" && (
                    <NeoButton 
                        variant="primary" 
                        size="sm" 
                        onClick={() => onUpdateStatus(ticket.id, "progress")}
                    >
                        <i className="ph-light ph-arrow-right me-1" /> Mover para Análise
                    </NeoButton>
                )}
                {ticket.status !== "resolved" && (
                    <NeoButton 
                        variant="success" 
                        size="sm" 
                        onClick={() => onUpdateStatus(ticket.id, "resolved")}
                    >
                        <i className="ph-light ph-check me-1" /> Marcar como Resolvido
                    </NeoButton>
                )}
            </div>
            <NeoButton variant="secondary" size="sm" onClick={toggle}>
                Fechar
            </NeoButton>
        </>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            title={`Chamado #${(ticket.id || "").substring(0, 6).toUpperCase()}`}
            subtitle={ticket.title}
            size="lg"
            footerActions={detailsFooter}
        >
            <div>
                {/* Info Cards */}
                <Row className="g-3 mb-4">
                    <Col sm={3}>
                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Academia</span>
                        <strong className="text-dark font-size-13">{ticket.tenantName || ticket.schoolName || "—"}</strong>
                    </Col>
                    <Col sm={2}>
                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Categoria</span>
                        <strong className="text-dark font-size-13">{ticket.category || "—"}</strong>
                    </Col>
                    <Col sm={2}>
                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Prioridade</span>
                        <NeoBadge color={PRIORITY_COLORS[ticket.priority] || "muted"}>
                            {ticket.priority}
                        </NeoBadge>
                    </Col>
                    <Col sm={2}>
                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Canal</span>
                        <strong className="text-dark font-size-13">{channelLabel}</strong>
                    </Col>
                    <Col sm={3}>
                        <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>Aberto em</span>
                        <strong className="text-dark font-size-13">
                            {ticket.createdAt
                                ? new Date(ticket.createdAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                                : ticket.date
                                ? new Date(ticket.date).toLocaleDateString("pt-BR")
                                : "—"}
                        </strong>
                    </Col>
                </Row>

                {/* Contato */}
                {(ticket.contactName || ticket.contactPhone || ticket.contactEmail) && (
                    <div className="p-3 bg-light rounded border mb-4">
                        <span className="text-xs text-muted d-block fw-bold text-uppercase mb-2" style={{ letterSpacing: "0.5px" }}>Contato do Solicitante</span>
                        <div className="d-flex gap-4 text-sm">
                            {ticket.contactName && <span><i className="mdi mdi-account me-1" />{ticket.contactName}</span>}
                            {ticket.contactPhone && <span><i className="mdi mdi-whatsapp me-1" />{ticket.contactPhone}</span>}
                            {ticket.contactEmail && <span><i className="mdi mdi-email-outline me-1" />{ticket.contactEmail}</span>}
                        </div>
                    </div>
                )}

                {/* Descrição */}
                <div className="p-3 bg-light rounded border mb-4">
                    <span className="text-xs text-muted d-block fw-bold text-uppercase mb-2" style={{ letterSpacing: "0.5px" }}>Descrição do Chamado</span>
                    <p className="m-0 text-sm text-dark" style={{ whiteSpace: "pre-line" }}>
                        {ticket.description || ticket.desc || "Sem descrição."}
                    </p>
                </div>

                {/* Comentários */}
                <h6 className="fw-bold text-dark mb-2">
                    Comentários Internos
                    <span className="text-muted fw-normal text-xs ms-2">({(ticket.comments || []).length})</span>
                </h6>
                <div className="mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {(ticket.comments || []).map((c, i) => (
                        <div key={i} className="p-2 border rounded mb-2 bg-light">
                            <div className="d-flex justify-content-between text-xs mb-1">
                                <strong>{c.user}</strong>
                                <span className="text-muted">{c.time}</span>
                            </div>
                            <p className="m-0 text-sm text-secondary">{c.text}</p>
                        </div>
                    ))}
                    {(ticket.comments || []).length === 0 && (
                        <p className="text-muted text-xs">Nenhum comentário interno cadastrado.</p>
                    )}
                </div>

                {/* Adicionar Comentário */}
                {ticket.status !== "resolved" && (
                    <div className="mb-0">
                        <NeoInput
                            label="Adicionar Anotação Interna"
                            type="text"
                            placeholder="Digite uma nota para a equipe de suporte..."
                            value={newComment}
                            onChange={(e) => onChangeComment(e.target.value)}
                        />
                        <div className="d-flex justify-content-end mt-2">
                            <NeoButton variant="primary" size="sm" onClick={onAddComment} disabled={!newComment?.trim()}>
                                Enviar Comentário
                            </NeoButton>
                        </div>
                    </div>
                )}
            </div>
        </NeoModal>
    );
};

TicketDetailModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    ticket: PropTypes.object,
    newComment: PropTypes.string.isRequired,
    onChangeComment: PropTypes.func.isRequired,
    onAddComment: PropTypes.func.isRequired,
    onUpdateStatus: PropTypes.func.isRequired
};

export default TicketDetailModal;
